import { useCallback, useEffect, useMemo, useState } from "react";
import type { CatalogueEntry } from "@dentacore/shared-types";

import { ApiError } from "../api/client";
import { addCatalogueToStock, listCatalogue } from "../api/suppliers";
import { LocationPriceDialog } from "./location-price-dialog";
import { useSeesPerUseCost } from "../inventory/use-sees-per-use-cost";
import inv from "../inventory/inventory.module.css";
import { ShelfList } from "../inventory/shelf-list";
import { leadText, packUsesText, perUseText } from "../inventory/supplier-text";
import styles from "../inventory/suppliers.module.css";
import { formatMoney } from "../patients/money";
import type { Writer } from "../settings/use-writer";
import { useCart } from "./cart";
import cat from "./catalogue.module.css";
import { StatusToggles, toggled } from "./status-toggles";
import { locate } from "../api/located";
import {
  Badges,
  CopySwitch,
  acrossRows,
  groupByName,
  locationName,
  useShown,
} from "../shell/locations";
import type { Located } from "../shell/locations";

/** A catalog row across the locations shown: one copy per location that lists it. */
type Row = CatalogueEntry & { at?: string; ats?: string[]; copies?: Located<CatalogueEntry>[] };

/**
 * **One row per item across the locations shown** (2026-10-22): the
 * same item at two locations is drawn once, its offers joined — one per supplier,
 * the lower price kept — and its stock added up.
 */
function merge(copies: Located<CatalogueEntry>[]): Row {
  const first = copies[0] as Located<CatalogueEntry>;
  if (copies.length === 1) return { ...first, ats: [first.at], copies };
  const bySupplier = new Map<string, CatalogueEntry["offers"][number]>();
  for (const copy of copies) {
    for (const offer of copy.offers) {
      const key = offer.supplierName.trim().toLowerCase();
      const had = bySupplier.get(key);
      if (had === undefined || offer.priceCents < had.priceCents) bySupplier.set(key, offer);
    }
  }
  const offers = [...bySupplier.values()];
  const low = Math.min(...offers.map((o) => o.priceCents));
  const held = copies.filter((copy) => copy.inStock !== null);
  const firstHeld = held[0]?.inStock ?? null;
  return {
    ...first,
    offers,
    cheapestIds: offers.filter((o) => o.priceCents === low).map((o) => o.id),
    inStock:
      firstHeld === null
        ? null
        : {
            ...firstHeld,
            quantityOnHand: held.reduce(
              (sum, copy) => sum + (copy.inStock?.quantityOnHand ?? 0),
              0,
            ),
            isLow: held.some((copy) => copy.inStock?.isLow === true),
            onOrder: held.some((copy) => copy.inStock?.onOrder === true),
            wantToBuy: held.some((copy) => copy.inStock?.wantToBuy === true),
          },
    ats: [...new Set(copies.map((copy) => copy.at))],
    copies,
  };
}

/** What a row's "Here" column says, as one word to filter on (proposal, 2026-10-17). */
type Status = "low" | "want" | "order" | "stock" | "none";
const STATUSES: readonly { value: Status; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "want", label: "Want to buy" },
  { value: "order", label: "On order" },
  { value: "stock", label: "In stock" },
  { value: "none", label: "Not held" },
];
/** Low even with an order on its way (Emile, 2026-10-17): it shows Low and Ordered. */
const isLowRow = (row: CatalogueEntry): boolean => row.inStock !== null && row.inStock.isLow;
const isWanted = (row: CatalogueEntry): boolean => row.inStock?.wantToBuy === true;
function hasStatus(row: CatalogueEntry, status: Status): boolean {
  if (status === "want") return isWanted(row);
  if (row.inStock === null) return status === "none";
  // A low item on order is both, so the Low count matches Inventory's.
  if (status === "low") return row.inStock.isLow;
  if (status === "order") return row.inStock.onOrder;
  // Held and not low, whether or not more is on its way (QF-D14) — as Inventory's shelf reads it.
  return status === "stock" && !row.inStock.isLow;
}

type Sort = "attention" | "name" | "cheapest" | "sellers";
const SORTS: readonly { value: Sort; label: string }[] = [
  { value: "attention", label: "Low first" },
  { value: "name", label: "Name A–Z" },
  { value: "cheapest", label: "Cheapest first" },
  { value: "sellers", label: "Most sellers" },
];
/**
 * **What a use costs at the cheapest seller** — the order *Cheapest first* sorts
 * by (QF-D13, as MOD-D22 first said). Comparing pack prices across items put a
 * 9.00 box of masks above 13.00 gloves that cost less a use. The figure itself is
 * never drawn without Accounting (MS-D11); only the order uses it.
 */
const cheapestPerUse = (row: CatalogueEntry): number =>
  Math.min(
    ...row.offers.map((o) => o.priceCents / Math.max(o.usesPerPack, 1)),
    Number.MAX_SAFE_INTEGER,
  );

/**
 * **The catalogue** — everything the clinic's own suppliers sell (STORY-189b,
 * MOD-D22).
 *
 * **Drawn the way Inventory is** (Emile, 2026-10-16: *"make it fit the theme of
 * other pages … it's taking too much to display one item"*): one row per item —
 * its cheapest price and who offers it, how many sell it, what this location
 * holds — and the row opens a panel beside the list with every supplier's offer
 * and *Add to cart*. It was one table row per item carrying every offer, three
 * lines each, so a page held one or two items.
 *
 * Only this clinic's suppliers appear; the server never returns another
 * clinic's. An item this location does not hold yet offers *Add to stock* in its
 * panel, which builds the stock item from the cheapest price line.
 */
export function CatalogueTab({
  currency,
  canManage,
  canOrder,
  busy,
  run,
  resetOn,
}: {
  currency: string;
  canManage: boolean;
  /** May put things in the cart (`inventory.movement.record`). */
  canOrder: boolean;
  busy: boolean;
  run: Writer["run"];
  /** Reloads when the location changes. */
  resetOn: string | null;
}): React.JSX.Element {
  const cart = useCart();
  // Price a use is a cost figure: only with Accounting (Emile, 2026-10-16).
  // Price per use needs Accounting and accounting.view (QF-D20).
  const hasAccounting = useSeesPerUseCost();

  const [rows, setRows] = useState<readonly Row[] | null>(null);
  // The locations shown, and the Catalog's own filter among them.
  const places = useShown();
  const [place, setPlace] = useState("");
  const [openAt, setOpenAt] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [supplier, setSupplier] = useState("");
  /** Several statuses at once, each toggled on or off (Emile, 2026-10-17). None on is all. */
  const [statuses, setStatuses] = useState<ReadonlySet<Status>>(new Set());
  const [sort, setSort] = useState<Sort>("attention");
  const [openKey, setOpenKey] = useState<string | null>(null);
  /** Add to stock, waiting on a price in this location's currency (IA-D9). */
  const [pricing, setPricing] = useState<{
    priceId: string;
    itemName: string;
    currency: string;
  } | null>(null);

  const reload = useCallback(
    async (signal?: AbortSignal) => {
      if (!places.many) {
        setRows(await listCatalogue(signal));
        return;
      }
      const all = await acrossRows(
        places.ids,
        () => listCatalogue(signal),
        (row, at) => {
          for (const offer of row.offers) locate(offer.id, at);
          if (row.inStock !== null) locate(row.inStock.itemId, at);
        },
      );
      setRows(groupByName(all, (row) => row.itemName).map((group) => merge(group.copies)));
    },
    [places.key],
  );

  useEffect(() => {
    const controller = new AbortController();
    setRows(null);
    reload(controller.signal).catch(() => {
      if (!controller.signal.aborted) setRows([]);
    });
    return () => controller.abort();
  }, [reload, resetOn]);

  const suppliers = useMemo(() => {
    const names = new Map<string, string>();
    for (const row of rows ?? []) {
      for (const offer of row.offers) names.set(offer.supplierId, offer.supplierName);
    }
    return [...names.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  /**
   * **Low first, then marked to buy** (Emile, 2026-10-17), the lowest count
   * first among the low; everything else in the server's order after. The
   * other sorts are a choice in the header, as is the status filter.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rank = (row: CatalogueEntry): number => (isLowRow(row) ? 0 : isWanted(row) ? 1 : 2);
    const compare: Record<Sort, (a: CatalogueEntry, b: CatalogueEntry) => number> = {
      attention: (a, b) =>
        rank(a) - rank(b) ||
        (rank(a) === 0 ? (a.inStock?.quantityOnHand ?? 0) - (b.inStock?.quantityOnHand ?? 0) : 0),
      name: (a, b) => a.itemName.localeCompare(b.itemName),
      cheapest: (a, b) => cheapestPerUse(a) - cheapestPerUse(b),
      sellers: (a, b) => b.offers.length - a.offers.length,
    };
    return (
      (rows ?? [])
        // One location chosen: its own copy of each item it lists.
        .flatMap((row): Row[] => {
          if (place === "") return [row];
          const copy = row.copies?.find((c) => c.at === place);
          return copy === undefined ? [] : [{ ...copy, ats: [copy.at], copies: row.copies }];
        })
        .filter(
          (row) =>
            (needle === "" || row.itemName.toLowerCase().includes(needle)) &&
            (supplier === "" || row.offers.some((o) => o.supplierId === supplier)) &&
            (statuses.size === 0 || [...statuses].some((on) => hasStatus(row, on))),
        )
        .map((row, index) => ({ row, index }))
        .sort((x, y) => compare[sort](x.row, y.row) || x.index - y.index)
        .map(({ row }) => row)
    );
  }, [rows, query, supplier, statuses, sort, place]);

  /** How many rows each status would show, for the filter's labels. */
  const statusCounts = useMemo(() => {
    const counts = new Map<Status, number>();
    for (const { value } of STATUSES) {
      counts.set(value, (rows ?? []).filter((row) => hasStatus(row, value)).length);
    }
    return counts;
  }, [rows]);

  /** The open item, at the location picked in its panel. */
  const openRow = shown.find((row) => row.itemKey === openKey) ?? null;
  const open: Row | null =
    openRow === null
      ? null
      : (openRow.copies?.find((copy) => copy.at === openAt) ??
        openRow.copies?.find((copy) => copy.at === place) ??
        openRow.copies?.[0] ??
        openRow);

  const addToCart = (row: CatalogueEntry, offerId: string): void => {
    const offer = row.offers.find((o) => o.id === offerId);
    if (offer === undefined || row.inStock === null) return;
    cart.add({
      inventoryItemId: row.inStock.itemId,
      itemName: row.itemName,
      supplierPriceId: offer.id,
      supplierId: offer.supplierId,
      supplierName: offer.supplierName,
      pack: offer.pack,
      usesPerPack: offer.usesPerPack,
      priceCents: offer.priceCents,
      cheapest: row.cheapestIds.includes(offer.id),
      packs: "1",
      reason: "",
    });
  };

  const listRows = shown.map((row) => {
    const best = row.offers.find((o) => row.cheapestIds.includes(o.id)) ?? row.offers[0];
    const on = row.itemKey === openKey;
    return {
      key: row.itemKey,
      node: (
        <button
          type="button"
          className={on ? `${cat.cells} ${cat.row} ${inv.rowOn}` : `${cat.cells} ${cat.row}`}
          aria-current={on ? "true" : undefined}
          onClick={() => {
            setOpenKey(on ? null : row.itemKey);
            setOpenAt(null);
          }}
          data-card=""
          data-testid={`catalogue-row-${row.itemKey}`}
        >
          <span className={inv.rowName}>
            {/* The shape that says "low" without colour, as on Inventory. */}
            <span className={row.inStock?.isLow === true ? `${inv.dot} ${inv.dotLow}` : inv.dot} />
            {row.inStock?.wantToBuy === true && (
              <span
                className={cat.star}
                role="img"
                aria-label="Marked to buy"
                title="Marked to buy"
              >
                ★
              </span>
            )}
            <span>{row.itemName}</span>
            <Badges ats={row.ats ?? []} shown={places} />
          </span>
          <span className={cat.best}>
            {best === undefined ? (
              "—"
            ) : (
              <>
                <b>{formatMoney(best.priceCents, currency)}</b>
                <span className={cat.bestFrom}>{best.supplierName}</span>
              </>
            )}
          </span>
          <span className={inv.rowCount}>{row.offers.length}</span>
          <span className={cat.here}>
            {row.inStock === null ? (
              <span className={cat.muted}>Not held</span>
            ) : row.inStock.isLow ? (
              // Low even with an order on its way; the Ordered tag lives on
              // Inventory's row and in this item's panel (Emile, 2026-10-17).
              <span className={`${styles.chip} ${styles.chipWarm}`}>
                Low · {row.inStock.quantityOnHand}
              </span>
            ) : row.inStock.onOrder ? (
              <span className={`${styles.chip} ${styles.chipOrder}`}>On order</span>
            ) : (
              <span>{row.inStock.quantityOnHand} in stock</span>
            )}
          </span>
        </button>
      ),
    };
  });

  return (
    <div className={inv.body} data-testid="catalogue-tab">
      <ShelfList
        testId="catalogue-list"
        head={
          <>
            <input
              type="search"
              className={`${inv.search} ${cat.search}`}
              value={query}
              placeholder="Search items…"
              aria-label="Search the catalog"
              data-testid="catalogue-search"
              onChange={(event) => setQuery(event.target.value)}
            />
            <select
              className={cat.select}
              value={supplier}
              aria-label="Only this supplier"
              data-testid="catalogue-supplier"
              onChange={(event) => setSupplier(event.target.value)}
            >
              <option value="">All suppliers</option>
              {suppliers.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            {places.many && (
              <select
                className={cat.select}
                value={place}
                aria-label="Only this location"
                data-testid="catalogue-location"
                onChange={(event) => setPlace(event.target.value)}
              >
                <option value="">All locations</option>
                {places.ids.map((id) => (
                  <option key={id} value={id}>
                    {locationName(places, id)}
                  </option>
                ))}
              </select>
            )}
            <StatusToggles
              options={STATUSES.map(({ value, label }) => ({
                value,
                label,
                count: statusCounts.get(value) ?? 0,
              }))}
              on={statuses}
              onToggle={(value) => setStatuses((was) => toggled(was, value as Status))}
              onClear={() => setStatuses(new Set())}
              testId="catalogue-status"
            />
            <select
              className={`${cat.select} ${cat.selectSmall}`}
              value={sort}
              aria-label="Sort by"
              data-testid="catalogue-sort"
              onChange={(event) => setSort(event.target.value as Sort)}
            >
              {SORTS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {rows !== null && (
              <span className={inv.headCount}>
                {shown.length} {shown.length === 1 ? "item" : "items"}
              </span>
            )}
          </>
        }
        columns={
          <div className={`${inv.columns} ${cat.cells}`} aria-hidden="true">
            <span>Item</span>
            <span>Cheapest</span>
            <span className={inv.columnRight}>Sellers</span>
            <span className={inv.columnRight}>Here</span>
          </div>
        }
        rows={listRows}
        empty={
          rows === null
            ? "Loading…"
            : rows.length === 0
              ? "Nothing yet. Add a supplier and their prices, and what they sell appears here."
              : "Nothing matches."
        }
        noun="items"
        resetOn={`${query}|${supplier}|${place}|${[...statuses].join(",")}|${sort}|${String(resetOn)}`}
      />

      {open !== null && (
        <aside className={`${inv.panel} ${cat.panel}`} data-testid="catalogue-panel">
          <div className={inv.panelHead}>
            <h2 className={inv.panelTitle}>{open.itemName}</h2>
            <button
              type="button"
              className={inv.close}
              onClick={() => setOpenKey(null)}
              aria-label="Close"
              data-testid="catalogue-panel-close"
            >
              ×
            </button>
          </div>

          {(openRow?.copies?.length ?? 0) > 1 && (
            <div className={cat.panelLocations}>
              <CopySwitch
                copies={(openRow?.copies ?? []).map((copy) => ({ ...copy, id: copy.at }))}
                current={open.at ?? null}
                shown={places}
                onPick={setOpenAt}
              />
            </div>
          )}
          <div className={cat.hereLine}>
            {open.inStock !== null ? (
              <span>
                {open.inStock.quantityOnHand} in stock{" "}
                {places.many && open.at !== undefined
                  ? `at ${locationName(places, open.at)}`
                  : "here"}{" "}
                {open.inStock.isLow && (
                  <span className={`${styles.chip} ${styles.chipWarm}`}>Low</span>
                )}{" "}
                {open.inStock.onOrder && (
                  <span className={`${styles.chip} ${styles.chipOrder}`}>
                    {open.inStock.isLow ? "Ordered" : "On order"}
                  </span>
                )}{" "}
                {open.inStock.wantToBuy && <span className={cat.star}>★ Marked to buy</span>}
              </span>
            ) : (
              <>
                <span className={cat.muted}>
                  Not held at{" "}
                  {places.many && open.at !== undefined
                    ? locationName(places, open.at)
                    : "this location"}
                </span>
                {canManage && (
                  <button
                    type="button"
                    className={inv.ghost}
                    disabled={busy}
                    data-testid={`catalogue-add-${open.itemKey}`}
                    onClick={() => {
                      const best = open.offers.find((o) => open.cheapestIds.includes(o.id));
                      const from = best ?? open.offers[0];
                      if (from === undefined) return;
                      void run(
                        async () => {
                          try {
                            return await addCatalogueToStock(from.id);
                          } catch (cause) {
                            // Another currency here: ask for the price rather than copy it.
                            if (
                              cause instanceof ApiError &&
                              cause.code === "PRICE_IN_LOCATION_CURRENCY"
                            ) {
                              const here = cause.details?.currency;
                              setPricing({
                                priceId: from.id,
                                itemName: open.itemName,
                                currency: typeof here === "string" ? here : currency,
                              });
                              return null;
                            }
                            throw cause;
                          }
                        },
                        () => reload(),
                        (added) =>
                          added === null
                            ? `Say what ${open.itemName} costs at this location.`
                            : `${open.itemName} is on this location's shelf.`,
                      );
                    }}
                  >
                    Add to stock
                  </button>
                )}
              </>
            )}
          </div>

          <h3 className={cat.sellersTitle}>Who sells it · {open.offers.length}</h3>
          {/*
            On order already (MOD-D24): no Add to cart, and the line says why — unless it
            is marked to buy, which is how more is ordered before the first lot arrives (WB-D5).
          */}
          {canOrder && open.inStock?.onOrder === true && (
            <p className={cat.orderNote} data-testid="catalogue-on-order-note">
              {open.inStock.wantToBuy
                ? "An order for this is already on its way. It is marked to buy, so you can order more now."
                : "An order for this is already on its way. Mark it Want to buy on Inventory to order more before it arrives."}
            </p>
          )}
          <ul className={cat.offers}>
            {open.offers.map((offer) => {
              const cheapest = open.cheapestIds.includes(offer.id) && open.offers.length > 1;
              // **Only what tells the suppliers apart** (Emile, 2026-10-16: "too much
              // information"): the pack only when they sell different packs, the
              // days to arrive only when the supplier gave them.
              const packsDiffer = new Set(open.offers.map((x) => x.pack)).size > 1;
              const details = [
                packsDiffer ? packUsesText(offer) : null,
                leadText(offer.leadDays),
              ].filter((part): part is string => part !== null);
              return (
                <li
                  key={offer.id}
                  className={cheapest ? `${cat.offer} ${cat.offerBest}` : cat.offer}
                >
                  <span className={cat.offerMain}>
                    {cheapest && (
                      <span className={cat.bestBadge}>
                        {open.cheapestIds.length > 1 ? "Same lowest price" : "Cheapest"}
                      </span>
                    )}
                    <span className={cat.offerName}>{offer.supplierName}</span>
                    {details.length > 0 && <span className={cat.muted}>{details.join(" · ")}</span>}
                  </span>
                  <span className={cat.offerPrice}>
                    <b>{formatMoney(offer.priceCents, currency)}</b>
                    {hasAccounting && (
                      <span className={cat.muted}>{perUseText(offer, currency)} a use</span>
                    )}
                  </span>
                  {canOrder &&
                    open.inStock !== null &&
                    (!open.inStock.onOrder || open.inStock.wantToBuy) && (
                      <button
                        type="button"
                        className={cheapest ? inv.primary : inv.ghost}
                        data-testid={`catalogue-cart-${offer.id}`}
                        disabled={cart.has(open.inStock.itemId)}
                        onClick={() => addToCart(open, offer.id)}
                      >
                        {cart.has(open.inStock.itemId) ? "In cart" : "Add to cart"}
                      </button>
                    )}
                </li>
              );
            })}
          </ul>
        </aside>
      )}
      {pricing !== null && (
        <LocationPriceDialog
          itemName={pricing.itemName}
          currency={pricing.currency}
          busy={busy}
          onClose={() => setPricing(null)}
          onSave={(priceCents) =>
            run(
              () => addCatalogueToStock(pricing.priceId, priceCents),
              () => reload(),
              `${pricing.itemName} is on this location's shelf.`,
            )
          }
        />
      )}
    </div>
  );
}
